import { FormBuilderService } from 'src/app/core/services/form-builder.service';
import {
  IFormbuilderFieldTypeConfiguration,
  ITimePickerFieldConfig,
} from '../../../../core/models/form-builder-types-configurations.model';
import { getFieldDisplayFormConfig } from '../form-fields-formly-display/form-fields-formly-display-field-generator.util';
import {
  baseFieldParseIntoSettingsFormModel,
  baseFieldUpdateFromSettingsFormModel,
  getDefaultBaseSettingsConfigFormly,
} from '../form-fields-formly-settings/form-builder-default-settings-configuration-formly-fields.utils';
import {
  getRequiredValidatorFormConfig,
  getValidationFieldsFormConfig,
} from '../form-fields-formly-validations/form-fields-formly-validation-field-generator.util';
import { fieldsBaseConfigurationFormModel } from './shared.configuration';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { AbstractControl, ValidationErrors } from '@angular/forms';
import { EIremboFormlyFieldTypes } from '@irembo-andela/irembogov3-common';

export const defaultTimePickerConfigFormModel: ITimePickerFieldConfig = {
  ...fieldsBaseConfigurationFormModel(),
  startHour: 0,
  endHour: 0,
  interval: 0,
};

export const timePickerFieldGetConfiguration = (
  formBuilderServiceInstance: FormBuilderService
): IFormbuilderFieldTypeConfiguration => {
  return {
    settings: {
      formModel: defaultTimePickerConfigFormModel,
      formlyForm: [
        ...getDefaultBaseSettingsConfigFormly(formBuilderServiceInstance),
        ...generateTimePickerForms(),
      ],
    },
    validations: {
      formModel: {},
      formlyForm: [
        getRequiredValidatorFormConfig(),
        getValidationFieldsFormConfig(formBuilderServiceInstance),
      ],
    },
    display: {
      formModel: {},
      formlyForm: [...getFieldDisplayFormConfig(formBuilderServiceInstance)],
    },
  };
};

export const timePickerFieldParseIntoSettingsFormModel = (
  field: FormlyFieldConfig
): ITimePickerFieldConfig => {
  const model = {
    ...baseFieldParseIntoSettingsFormModel(field),
    startHour: field.props?.['startHour'] ?? 0,
    endHour: field.props?.['endHour'] ?? 0,
    interval: field.props?.['interval'] ?? 0,
  } as ITimePickerFieldConfig;

  return model;
};

export const timePickerFieldUpdateFromSettingsFormModel = (
  model: ITimePickerFieldConfig,
  field: FormlyFieldConfig
): FormlyFieldConfig => {
  const timePickerField = baseFieldUpdateFromSettingsFormModel(model, field);

  if (timePickerField.props) {
    timePickerField.props['startHour'] = model?.startHour;
    timePickerField.props['endHour'] = model?.endHour;
    timePickerField.props['interval'] = model?.interval;
  }

  return timePickerField;
};

export const generateTimePickerForms = (): FormlyFieldConfig[] => {
  return [
    {
      id: 'timePickerSetting',
      type: EIremboFormlyFieldTypes['formly-group'],
      wrappers: ['panel-wrapper'],
      validators: {
        validation: [startEndHourValidator],
      },
      props: {
        dragDisabled: false,
        label: 'Time picker settings',
        collapsible: true,
        collapsed: true,
      },
      fieldGroup: [
        {
          key: 'startHour',
          type: 'number',
          props: {
            label: 'Start hour (24 Hour)',
            placeholder: 'Provide start hour',
            min: 0,
            max: 23,
          },
          validation: {
            messages: {
              min: 'Minimum value is 0',
              max: 'Maximum value is 23',
              required: 'This field is required',
            },
          },
          expressions: {
            'props.required': '!field.hide',
          },
        },
        {
          key: 'endHour',
          type: 'number',
          props: {
            label: 'End Hour (24 Hour)',
            placeholder: 'Provide end hour',
            min: 0,
            max: 23,
            required: true,
          },
          validation: {
            messages: {
              min: 'Minimum value is 0',
              max: 'Maximum value is 23',
              time: 'End hour must be after start hour',
              required: 'This field is required',
            },
          },
          validators: {
            startLessThanEnd: {
              expression: (control: AbstractControl) => {
                const start = control.get('startHour')?.value;
                const end = control.get('endHour')?.value;
                if (start == null || end == null) return true;

                return start <= end;
              },
              message: 'Start hour must be less than or equal to End hour',
            },
          },
        },
        {
          key: 'interval',
          type: 'number',
          props: {
            label: 'Interval',
            placeholder: 'Enter interval',
            min: 0,
            max: 60,
            required: true,
          },
          validation: {
            messages: {
              min: 'Minimum value is 0',
              max: 'Maximum value is 60',
              required: 'This field is required',
            },
          },
          expressions: {
            hide: '!(model?.startHour && model?.endHour)',
            'props.required': '!field.hide',
          },
        },
      ],
    },
  ];
};

const startEndHourValidator = (
  control: AbstractControl,
  _field: FormlyFieldConfig,
  _options: any
): ValidationErrors | null => {
  const formData = control.value;
  const startHour = formData.startHour;

  const endHour = formData.endHour;
  if (endHour == null || startHour == null) return null;

  return startHour > endHour
    ? {
        startendhour: {
          errorPath: 'endHour',
          message: 'End hour must be after start hour',
        },
      }
    : null;
};
